import type {
  ActivityContact,
  EmbedCookie,
  Listing,
  ListingBroker,
  ModelCompany,
  ModelOrganization,
  ModelUser,
  TokenUser,
  User,
} from '~/models'

export enum ActivitySource {
  admin = 'admin',
  app = 'app',
}

export enum ActivityType {
  create = 'create',
  update = 'update',
  dataUpdate = 'dataUpdate',
  delete = 'delete',
  view = 'view',
  preview = 'preview',
  download = 'download',
  import = 'import',
  contact = 'contact',
  login = 'login',
  viewLink = 'viewLink',
  notification = 'notification',
  emailLink = 'emailLink',
  emailOpen = 'emailOpen',
}

export enum ActivityRelatedType {
  organization = 'organization',
  broker = 'broker',
  contact = 'contact',
  contactList = 'contactList',
  company = 'company',
  dataLayer = 'dataLayer',
  dataLayerEntry = 'dataLayerEntry',
  email = 'email',
  listing = 'listing',
  memberRequest = 'memberRequest',
  report = 'report',
  search = 'search',
  user = 'user',
}

export type Activity = {
  _id: string
  tid: string
  organization: ModelOrganization
  created: string
  user?: TokenUser
  historyId?: string
  source: ActivitySource
  type: ActivityType
  relatedType: ActivityRelatedType
  related: {
    _id: string
    tid: string
    slug: string
    name?: string
    firstName?: string
    lastName?: string
    company?: ModelCompany
    organization: ModelOrganization
  }
  embed?: EmbedCookie
  contact?: ActivityContact
  brokers?: (ListingBroker | User | ModelUser)[]
  meta?: { [key: string]: any }
  referer?: string
  userAgent?: string
  ipAddress?: string
  hidden?: boolean
  geolocation?: {
    city: string
    stateAbbr?: string
    country: string
    latitude: string
    longitude: string
  }
}

export type ListingActivityProjection = Pick<
  Listing,
  '_id' | 'name' | 'slug' | 'property' | 'images' | 'company' | 'brokers'
>

export type ActivityWithListing<
  TActivity = Activity,
  TListing = ListingActivityProjection | null,
> = TActivity & {
  listing?: TListing | null
}

export type ActivityExportProjection = {
  _id: string
  created: string
  type: ActivityType
  related: Pick<Activity['related'], '_id' | 'name' | 'slug'>
  user?: Pick<
    TokenUser,
    '_id' | 'firstName' | 'lastName' | 'email' | 'phone' | 'organization'
  >
  geolocation?: Pick<NonNullable<Activity['geolocation']>, 'city' | 'stateAbbr'>
  embed?: Pick<EmbedCookie, 'hostURL'>
  meta?: { emailSubject?: string }
}

export type ActivityReportMetrics = {
  views: number
  downloads: number
  links: number
  emailClicks: number
  total: number
  firstCreated?: string
  lastCreated?: string
}

export type ActivityReportChartPoint = {
  date: string
  views: number
  downloads: number
  links: number
  emailClicks: number
}

export type ActivityReportLocation = {
  city: string
  stateAbbr?: string
  count: number
}

export type ActivityReportAggregate = {
  metrics: ActivityReportMetrics
  chart: ActivityReportChartPoint[]
  locations: ActivityReportLocation[]
}

export type ActivityData = Pick<
  Activity,
  | 'organization'
  | 'source'
  | 'type'
  | 'relatedType'
  | 'related'
  | 'contact'
  | 'brokers'
  | 'meta'
  | 'historyId'
>

export function excludeActivityCompanyUsers(
  activity: Activity,
  listing: Listing
) {
  const brokerIds = listing.brokers.map((broker) => broker._id)
  const isListingBroker = activity.user && brokerIds.includes(activity.user._id)

  if (
    [ActivityType.create, ActivityType.update, ActivityType.delete].includes(
      activity.type
    )
  ) {
    return true
  } else if (
    isListingBroker ||
    listing.company._id === activity.user?.company?._id
  ) {
    return false
  }
  return true
}
